import React, { useState, useEffect } from "react";

interface DateRange {
  startDate: string;
  endDate: string;
}

interface DateSearchBtnProps {
  onDateSelect: (range: DateRange) => void;
  mode?: "range" | "single";
  className?: string;
  initialValue?: string; // 비제어형 기본 선택 id
  value?: string; // 제어형 선택 id
  onChangeValue?: (id: string) => void; // 제어형 변경 핸들러
  currentRange?: { startDate?: string; endDate?: string }; // range 모드용 현재 범위
  currentDate?: string; // single 모드용 현재 날짜
}

const formatDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function DateSearchBtn({
  onDateSelect,
  className = "",
  mode = "range",
  initialValue = "",
  value,
  onChangeValue,
  currentRange,
  currentDate,
}: DateSearchBtnProps) {
  const [selectedButton, setSelectedButton] = useState<string>(initialValue || "today");

  // 초기값 변화 반영 (비제어형 사용 시)
  useEffect(() => {
    if (initialValue) setSelectedButton(initialValue);
  }, [initialValue]);

  // 제어형 사용 시, 외부 value를 우선
  const effectiveSelected = value ?? selectedButton;

  const buttons = [
    { id: "prevMonth", label: "-1개월" },
    { id: "yesterday", label: "전일" },
    { id: "today", label: "금일" },
    { id: "tomorrow", label: "익일" },
    { id: "nextMonth", label: "+1개월" },
  ];

  const daysInMonth = (y: number, m0: number) => new Date(y, m0 + 1, 0).getDate(); // m0: 0-based
  const addMonthsKeepDay = (d: Date, n: number) => {
    const y = d.getFullYear();
    const m0 = d.getMonth() + n;
    const targetY = y + Math.floor(m0 / 12);
    const targetM0 = ((m0 % 12) + 12) % 12;
    const targetLast = daysInMonth(targetY, targetM0);
    const day = Math.min(d.getDate(), targetLast);
    return new Date(targetY, targetM0, day);
  };

  const parseDate = (s: string) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  };
  const firstDayOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
  const lastDayOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);

  const handleButtonClick = (buttonId: string) => {
    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);

    // single 모드: prev/nextMonth는 현재 날짜 기준 월 이동 (일 보정)
    if (mode === "single" && (buttonId === "prevMonth" || buttonId === "nextMonth")) {
      const base = currentDate ? parseDate(currentDate) : today;
      const moved = addMonthsKeepDay(base, buttonId === "prevMonth" ? -1 : +1);

      if (onChangeValue) onChangeValue(buttonId);
      else setSelectedButton(buttonId);

      const newStr = formatDate(moved);
      onDateSelect({ startDate: newStr, endDate: newStr });
      return;
    }

    // range 모드: 이전에 만든 월 전체(1일~말일) 이동 로직
    if (mode === "range" && (buttonId === "prevMonth" || buttonId === "nextMonth")) {
      // 기준일: startDate 우선, 없으면 endDate, 둘 다 없으면 오늘
      const base =
        (currentRange?.startDate && parseDate(currentRange.startDate)) ||
        (currentRange?.endDate && parseDate(currentRange.endDate)) ||
        today;

      const targetMonth = buttonId === "prevMonth" ? addMonths(base, -1) : addMonths(base, +1);
      const startOfTarget = firstDayOfMonth(targetMonth);
      const endOfTarget = lastDayOfMonth(targetMonth);

      if (onChangeValue) onChangeValue(buttonId);
      else setSelectedButton(buttonId);

      onDateSelect({
        startDate: formatDate(startOfTarget),
        endDate: formatDate(endOfTarget),
      });
      return;
    }

    // 전일/금일/익일은 기존 동작 유지 (single/range 공통)
    switch (buttonId) {
      case "yesterday":
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
        break;
      case "today":
        break;
      case "tomorrow":
        start.setDate(start.getDate() + 1);
        end.setDate(end.getDate() + 1);
        break;
      case "prevMonth":
      case "nextMonth":
        break;
    }

    if (onChangeValue) onChangeValue(buttonId);
    else setSelectedButton(buttonId);

    onDateSelect(
      mode === "single"
        ? { startDate: formatDate(end), endDate: formatDate(end) }
        : { startDate: formatDate(start), endDate: formatDate(end) }
    );
  };

  return (
    <div className={`${className}`}>
      {/* 라벨 영역 공간 (DateBox와 높이 맞추기) */}
      <div className="h-6 mb-1"></div>

      {/* 버튼 그룹 */}
      <div className="flex">
        {buttons.map((button, index) => (
          <button
            key={button.id}
            onClick={() => handleButtonClick(button.id)}
            className={`
              px-3 py-2 text-base font-medium leading-normal cursor-pointer
              border border-[#CCCCCC] transition-colors min-w-[60px] flex items-center justify-center
              ${
                effectiveSelected === button.id
                  ? "bg-[#1E380B] text-white border-[#1E380B]"
                  : "bg-white text-[#333333] hover:bg-gray-50"
              }
              ${index === 0 ? "rounded-l-md" : ""}
              ${index === buttons.length - 1 ? "rounded-r-md" : ""}
              ${index > 0 ? "-ml-px" : ""}
            `}
          >
            {button.label}
          </button>
        ))}
      </div>
    </div>
  );
}
